import { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { FONT_SANS, SQRT3, linesForCells, sizeFromCellCount, v } from './constants';
import { applyModifier, badRestrictionCells, checkRestriction, checkSolved, computeConstraintStatuses, computeLineStats, findHintMove, solveRemaining, type HintMove } from './rules';
import { dateFromPuzzleDate, gameReducer, loadStreak, loadTheme, markPlayed, newPuzzleState, saveSolvedGame, saveStreak, saveTheme, type StreakInfo } from './state';
import { dailyDateString } from './generator';
import type { Difficulty } from './types';
import { BagTile, HexBoard, type ActiveConstraint, type DragState } from './components/HexBoard';
import { Bag } from './components/Bag';
import { DifficultyTabs, MarkLegend, Wordmark } from './components/ui';
import { HelpMenuModal, HowToPlayModal, WinModal, formatTime } from './components/modals';
import { Tutorial } from './components/Tutorial';
import { AuthModal } from './components/AuthModal';
import { useAuth } from './auth/AuthContext';
import { recordSolve } from './lib/solves';
import { openCustomerPortal } from './lib/billing';
import { clearProgress, loadProgress, saveProgress } from './lib/progress';
import { fetchHintBalance, spendHint } from './lib/hints';
import { CheckoutModal, type CheckoutKind } from './components/CheckoutModal';
import { AccountModal } from './components/AccountModal';
import { HintArrow } from './components/HintArrow';
import { ArchiveView } from './components/ArchiveView';
import { BadgeAchievementModal } from './components/BadgeAchievementModal';
import { SharedHeader } from './components/SharedHeader';
import { IosAppBanner } from './components/IosAppBanner';
import { DailyLeaderboardModal } from './components/DailyLeaderboardModal';
import { Haptics } from './lib/haptics';
import type { Badge } from './lib/badges';

const DRAG_THRESHOLD = 5;

export default function App() {
  // Theme
  const [theme, setTheme] = useState<'light' | 'dark'>(loadTheme);
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    saveTheme(theme);
  }, [theme]);

  const [state, dispatch] = useReducer(gameReducer, 'Easy' as Difficulty, newPuzzleState);
  // Help surface state machine:
  //   showHelpMenu → the two-option chooser (replay tutorial / show text)
  //   showHelpText → the legacy wall-of-text reference
  //   showTutorial → the interactive guided puzzle
  // First-time visitors skip the menu and land directly in the tutorial.
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showAuth, setShowAuth] = useState<false | 'login' | 'signup'>(false);
  const [showArchive, setShowArchive] = useState(false);
  const [streakInfo, setStreakInfo] = useState<StreakInfo>(loadStreak);
  const [winModalDismissed, setWinModalDismissed] = useState(false);
  // Daily leaderboard modal — opens with the current (puzzleDate,
  // difficulty) from WinModal's "See today's leaderboard" button.
  // Null when closed.
  const [showDailyLeaderboard, setShowDailyLeaderboard] = useState(false);
  const [activeConstraint, setActiveConstraint] = useState<ActiveConstraint | null>(null);
  // Index of the currently-focused board cell. Click an empty/locked
  // cell with no tile selected to focus it; clicking again clears.
  // Drives constraint-chip + bag-tile highlighting via the
  // highlightedTileIds memo below and the HexBoard prop.
  const [focusedCellIdx, setFocusedCellIdx] = useState<number | null>(null);
  // Win-celebration state: epicenter cell for the ripple, plus a flag the
  // WinModal waits on so the animation gets to play first.
  const [winEpicenter, setWinEpicenter] = useState<number | null>(null);
  const [winAnimDone, setWinAnimDone] = useState(false);
  // FIFO queue of badges just-unlocked by a solve. The head is rendered
  // by BadgeAchievementModal; dismissing pops the queue. Earned during
  // gameplay, surfaced over the WinModal.
  const [badgeQueue, setBadgeQueue] = useState<Badge[]>([]);
  const auth = useAuth();

  // Reset the WinModal-dismissed flag whenever the player moves to a
  // fresh puzzle context — unsolved-state OR a different (date, diff)
  // entirely. Without the puzzleDate/difficulty deps, swapping straight
  // from a solved Easy to an already-solved Medium kept the dismissed
  // flag stale, which is fine; the bug we're guarding against is more
  // subtle — the flag also masked the legitimate next-solve modal on
  // some flows. Cheap to reset; the modal will re-render naturally.
  useEffect(() => {
    if (!state.solved) setWinModalDismissed(false);
  }, [state.solved, state.puzzleDate, state.difficulty]);

  // Drive the win-ripple animation: capture the epicenter (last placed cell)
  // and let the ripple play before the WinModal pops. `prev ??` preserves
  // the epicenter when `state.recentlyPlaced` clears mid-animation via
  // the 320ms CLEAR_PLACE_HIGHLIGHT timer (else the ripple's centre
  // would jump from the player's last move to the grid centre).
  useEffect(() => {
    if (state.solved) {
      setWinEpicenter(prev => prev ?? state.recentlyPlaced ?? Math.floor(state.cells.length / 2));
      const t = setTimeout(() => setWinAnimDone(true), 1500);
      return () => clearTimeout(t);
    } else {
      setWinEpicenter(null);
      setWinAnimDone(false);
    }
  }, [state.solved, state.recentlyPlaced, state.cells.length]);

  // Hard-reset all win-animation state when the puzzle context changes
  // (date or difficulty). Without this, swapping mid-ripple onto an
  // already-solved difficulty kept the stale epicenter index pointing at
  // a cell that didn't exist on the new grid (Easy 7 → Medium 19), and
  // the WinModal could re-pop briefly because the dismissed flag and
  // winAnimDone flag carried over. Belt-and-suspenders with the
  // dismissed-reset effect below.
  useEffect(() => {
    setWinEpicenter(null);
    setWinAnimDone(false);
  }, [state.puzzleDate, state.difficulty]);

  // Timer
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (state.solved) return;
    const iv = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(iv);
  }, [state.solved, state.startedAt]);
  const elapsed = state.solved ? state.elapsedAtSolve : (now - state.startedAt);

  // Show the interactive tutorial on first ever visit. Keyed under
  // `hm.seenTutorial` so users who only saw the legacy text wall (under
  // the old `hm.seenHelp` flag) still get the new tutorial once.
  useEffect(() => {
    try {
      if (!localStorage.getItem('hm.seenTutorial')) {
        setShowTutorial(true);
        localStorage.setItem('hm.seenTutorial', '1');
      }
    } catch {/* ignore */}
  }, []);

  const evaluatedConstraints = useMemo(
    () => computeConstraintStatuses(state.cells, state.constraints),
    [state.cells, state.constraints]
  );
  const badRestr = useMemo(() => badRestrictionCells(state.cells), [state.cells]);

  useEffect(() => {
    if (state.solved) return;
    if (checkSolved(state.cells, evaluatedConstraints, state.bag)) {
      const ms = Date.now() - state.startedAt;
      dispatch({ type: 'MARK_SOLVED', elapsed: ms });
      Haptics.solved();
      // Persist the finished board so a reload brings the user back to the
      // solved state instead of a fresh puzzle. Key by `state.puzzleDate`
      // (not the implicit "now") so winning an archived puzzle doesn't
      // shadow today's key — that's how the bug "play archive → press
      // Today → today shows archive tiles" reproduced.
      saveSolvedGame(state.difficulty, {
        cells: state.cells,
        bag: state.bag,
        constraints: state.constraints,
        name: state.name,
        elapsedAtSolve: ms,
      }, dateFromPuzzleDate(state.puzzleDate));
      // Anonymous: credit today as a played-day for the header streak.
      // Signed in: post the solve to the server, server stats becomes
      // the source of truth (handled below).
      if (!auth.user) {
        setStreakInfo(prev => {
          const updated = markPlayed(prev, state.puzzleDate);
          saveStreak(updated);
          return updated;
        });
      } else {
        const puzzleDate = state.puzzleDate;
        recordSolve({ puzzleDate, difficulty: state.difficulty, elapsedMs: ms })
          .then(r => {
            if (!r) return;
            auth.setStats(r.stats);
            if (r.newBadges.length > 0) {
              setBadgeQueue(prev => [...prev, ...r.newBadges]);
            }
          })
          .catch(() => {/* ignore — game continues, stats just won't update this round */});
      }
    }
  }, [state.cells, state.bag, evaluatedConstraints, state.solved, state.startedAt, auth, state.difficulty]);

  useEffect(() => {
    if (state.recentlyPlaced == null) return;
    const t = setTimeout(() => dispatch({ type: 'CLEAR_PLACE_HIGHLIGHT' }), 320);
    return () => clearTimeout(t);
  }, [state.recentlyPlaced]);

  // Duolingo-style streak credit: anonymous users get today added to
  // their played-days set the moment the app loads — no board action
  // required, just opening the site counts. Signed-in users let the
  // server compute streak from their UserDailyVisit ledger (written by
  // the requireAuth middleware on /me + every other authenticated
  // endpoint) so the same "opened the app today" trigger applies.
  useEffect(() => {
    if (auth.user) return;
    setStreakInfo(prev => {
      const updated = markPlayed(prev, dailyDateString());
      if (updated === prev) return prev;
      saveStreak(updated);
      return updated;
    });
    // Re-run when auth resolves to anonymous, when the daily rolls
    // over (puzzleDate is today's NY date), and when the user signs out
    // mid-session. No board-state deps — opening the site is enough.
  }, [auth.user, state.puzzleDate]);

  // ─────────────── Server progress sync ───────────────
  // Load saved progress whenever the signed-in user or difficulty
  // changes. The fresh puzzle is rendered immediately (so anonymous +
  // first-time signed-in users see today's grid right away), then the
  // saved snapshot — if any — is dispatched on top.
  useEffect(() => {
    if (!auth.user) return;
    let cancelled = false;
    const date = state.puzzleDate;
    const diff = state.difficulty;
    loadProgress(date, diff)
      .then(prog => {
        if (cancelled || !prog) return;
        // Guard: only apply if we're still on the same difficulty/date —
        // user could have switched while the request was in flight.
        if (prog.difficulty !== diff || prog.puzzleDate !== date) return;
        dispatch({
          type: 'LOAD_PROGRESS',
          cells: prog.cells,
          bag: prog.bag,
          solved: prog.solved,
          elapsedMs: prog.elapsedMs,
        });
      })
      .catch(() => { /* network — leave the freshly-generated puzzle in place */ });
    return () => { cancelled = true; };
  }, [auth.user, state.difficulty]);

  // Save progress on every cells/bag change, debounced. Only signed-in
  // users sync to the server; anonymous players keep their state in
  // memory until they sign in (no anonymous→signed-in migration today).
  useEffect(() => {
    if (!auth.user) return;
    const snapshot = JSON.stringify({ cells: state.cells, bag: state.bag, solved: state.solved });
    if (snapshot === lastSavedRef.current) return;
    const handle = setTimeout(() => {
      const date = state.puzzleDate;
      saveProgress(date, state.difficulty, {
        cells: state.cells,
        bag: state.bag,
        solved: state.solved,
        elapsedMs: state.solved ? state.elapsedAtSolve : undefined,
      })
        .then(ok => { if (ok) lastSavedRef.current = snapshot; })
        .catch(() => {/* swallow — gameplay continues, next change retries */});
    }, 600);
    return () => clearTimeout(handle);
  }, [auth.user, state.cells, state.bag, state.solved, state.elapsedAtSolve, state.difficulty]);

  const clickableCells = useMemo(() => {
    const set = new Set<number>();
    state.cells.forEach((c, i) => {
      // Every non-gap cell is clickable. Placed → pickup, empty + tile
      // selected → place, anything else → toggle focus highlight. The
      // disambiguation lives in handleCellClick.
      if (c.kind !== 'gap') set.add(i);
    });
    return set;
  }, [state.cells]);

  const draggableCells = useMemo(() => {
    const set = new Set<number>();
    state.cells.forEach((c, i) => {
      if (c.kind === 'placed') set.add(i);
    });
    return set;
  }, [state.cells]);

  const linesPassing = evaluatedConstraints.filter(c => c.status === 'ok').length;
  const tilesPlaced = state.bag.filter(t => t.placed).length;

  // Bag tiles that fit either the active (hover/pin) constraint OR any
  // constraint passing through the focused cell. Categorical only —
  // sum/product depend on a partial-line solver we don't ship yet. The
  // focused-cell branch additionally filters by the cell's own
  // restriction so the player sees only tiles that could legally land
  // in that slot.
  const highlightedTileIds = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    type CT = NonNullable<typeof activeConstraint>['type'];
    const matches = (val: number, type: CT, val2?: number | number[]): boolean => {
      switch (type) {
        case 'allEven': return val % 2 === 0;
        case 'allOdd':  return Math.abs(val % 2) === 1;
        case 'noNeg':   return val >= 0;
        case 'allNeg':  return val < 0;
        case 'contains':
          return Array.isArray(val2) && val2.includes(val);
        default: return false;
      }
    };

    // Active (hover/pin) constraint path — original behavior.
    if (activeConstraint) {
      state.bag.forEach(t => {
        if (!t.placed && matches(t.value, activeConstraint.type as CT, activeConstraint.value)) {
          set.add(t.id);
        }
      });
    }

    // Focused-cell path: highlight tiles that COULD legally land in
    // the focused cell. A tile qualifies when:
    //   - it satisfies the cell's own restriction (Even/Odd/Pos/etc),
    //     AND
    //   - placing it there doesn't immediately violate any constraint
    //     on any line through the cell. "Immediately violate" =
    //     computeConstraintStatuses returns 'bad' for that constraint
    //     after the hypothetical placement (line is full + wrong
    //     total, e.g. Σ=6 line with cells [3,?,1] rejects everything
    //     but 2). Lines that are still partially empty stay 'pending'
    //     so we accept the tile — we can't prove it won't work.
    //
    // 2026-05-13 fix: previously only categorical constraints
    // (Even/Odd/Pos./All Neg./contains) drove the highlight set.
    // Numeric constraints (Σ/×/≥/≤/N Neg.) returned `false` from the
    // matches() switch so puzzles built entirely of numeric chips
    // never lit anything up. The hypothetical-placement check above
    // covers BOTH families with a single code path.
    if (focusedCellIdx != null) {
      const lines = linesForCells(state.cells.length);
      const focusedConstraints = evaluatedConstraints.filter(c =>
        lines[c.axis][c.index].includes(focusedCellIdx),
      );
      const focusedCell = state.cells[focusedCellIdx];
      const restriction = focusedCell?.kind === 'empty' ? (focusedCell._restriction ?? null) : null;

      state.bag.forEach(t => {
        if (t.placed) return;
        if (restriction && !checkRestriction(t.value, restriction)) return;

        // Quick categorical-only short-circuit (avoids constructing a
        // full hypothetical board when we already know the answer).
        for (const c of focusedConstraints) {
          if (matches(t.value, c.type as CT, c.value)) { set.add(t.id); return; }
        }

        // Hypothetical placement: copy the cell, swap to a placed
        // version using the tile's value, re-run the constraint
        // evaluator scoped to the lines through this cell. Any 'bad'
        // status disqualifies the tile.
        if (focusedCell && focusedCell.kind === 'empty') {
          const hypothetical = state.cells.slice();
          hypothetical[focusedCellIdx] = {
            kind: 'placed',
            value: t.value,
            placedTileId: t.id,
            ...(focusedCell.modifier != null ? { modifier: focusedCell.modifier } : {}),
            ...(focusedCell._restriction != null ? { _restriction: focusedCell._restriction } : {}),
          };
          const evals = computeConstraintStatuses(hypothetical, state.constraints);
          // Only check the constraints that touch this cell — others
          // are unaffected by the placement.
          const touched = evals.filter(c =>
            lines[c.axis][c.index].includes(focusedCellIdx),
          );
          const violates = touched.some(c => c.status === 'bad');
          if (!violates) set.add(t.id);
        }
      });
    }
    return set;
  }, [activeConstraint, focusedCellIdx, evaluatedConstraints, state.bag, state.cells, state.constraints]);

  const brokenBanner = useMemo(() => {
    const broken = evaluatedConstraints.find(c => c.status === 'bad');
    if (!broken) {
      if (badRestr.size > 0) {
        const i = [...badRestr][0];
        const cell = state.cells[i];
        const r = cell._restriction;
        const desc = r ? ({
          even: 'must be even', odd: 'must be odd',
          pos: 'must be positive', neg: 'must be negative',
          prime: 'must be prime', magHi: 'magnitude must be ≥ 5', magLo: 'magnitude must be ≤ 4',
        } as const)[r] : 'restricted';
        return { tag: `cell · ${i + 1}`, msg: `Tile breaks restriction (${desc}).` };
      }
      return null;
    }
    const axisLabel = ({ col: 'Column', nwse: 'NW-SE', nesw: 'NE-SW' } as const)[broken.axis];
    const tag = `${axisLabel} · ${broken.index + 1}`;
    const stats = computeLineStats(state.cells, linesForCells(state.cells.length)[broken.axis][broken.index]);
    let msg: string;
    switch (broken.type) {
      case 'sum':
        msg = `Sums to ${stats.sum}, should sum to ${broken.value}.`; break;
      case 'product':
        msg = `Product is ${stats.product}, should be ${broken.value}.`; break;
      case 'sumGte':
        msg = `Sums to ${stats.sum}, must be at least ${broken.value}.`; break;
      case 'sumLte':
        msg = `Sums to ${stats.sum}, must be at most ${broken.value}.`; break;
      case 'allEven':
        msg = 'Contains an odd value — must be all even.'; break;
      case 'allOdd':
        msg = 'Contains an even value — must be all odd.'; break;
      case 'noNeg':
        msg = `Contains ${stats.negCount} negative${stats.negCount === 1 ? '' : 's'} — must be all non-negative.`; break;
      case 'allNeg':
        msg = 'Contains a non-negative — must be all negative.'; break;
      case 'negCount':
        msg = `Has ${stats.negCount} negative${stats.negCount === 1 ? '' : 's'}, should have ${broken.value}.`; break;
      default:
        msg = 'Constraint broken.';
    }
    return { tag, msg };
  }, [evaluatedConstraints, badRestr, state.cells]);

  // Last serialised cells+bag we successfully saved to the server.
  // Declared up here so handleReset (below) can clear it.
  const lastSavedRef = useRef<string>('');

  // White-label embedded checkout. The `Upgrade` button opens a tier
  // picker (monthly vs yearly); picking either swaps the picker out
  // for the embedded Stripe payment form (CheckoutModal) — the user
  // never leaves hexponents.com. Hint pack uses the same modal with
  // a different `product` shape.
  const [showUpgradePicker, setShowUpgradePicker] = useState(false);
  const [activeCheckout, setActiveCheckout] = useState<CheckoutKind | null>(null);
  const [showAccount, setShowAccount] = useState(false);

  const handleUpgrade = useCallback(() => {
    if (!auth.user) { setShowAuth('login'); return; }
    if (auth.user.isPremium) {
      // Already paying — open the in-app Account view (white-label
      // change-card / cancel UI) instead of starting another sub.
      setShowAccount(true);
      return;
    }
    setShowUpgradePicker(true);
  }, [auth.user]);

  const pickAndCheckout = useCallback((cadence: 'monthly' | 'yearly') => {
    setShowUpgradePicker(false);
    setActiveCheckout({ kind: 'subscription', cadence });
  }, []);

  const handleCheckoutSuccess = useCallback(() => {
    setActiveCheckout(null);
    // Webhook is the source of truth — poll /me until the entitlement
    // catches up (usually within a couple seconds of stripe.confirmPayment).
    let attempts = 0;
    const tick = async () => {
      await auth.refresh();
      const fresh = await fetchHintBalance();
      if (fresh) auth.setHintsBalance(fresh.balance);
      if (++attempts < 6) setTimeout(tick, 1500);
    };
    tick();
  }, [auth]);

  const handleManageSubscription = useCallback(async () => {
    const p = await openCustomerPortal();
    if (p.ok) window.location.href = p.url;
    else alert('Could not open subscription management.');
  }, []);

  // Hint UI (redesigned 2026-05-12): instead of just pulsing an arrow,
  // the hint now AUTO-EXECUTES the move it found — places the right
  // tile in the right cell, or picks up a wrong placement. The brief
  // "recentlyPlaced" highlight already fires from the reducer and gives
  // the player visual feedback on what happened. Pedagogically this is
  // a much bigger payoff for the hint cost; for guidance without paying,
  // players have the new "click a cell to see what fits" focus mode.
  //
  // `hint` state is retained as a no-op for now so the old type +
  // HintArrow imports stay happy — the arrow render is gated below.
  const [hint] = useState<HintMove | null>(null);
  const handleHint = useCallback(async () => {
    if (!auth.user) { setShowAuth('login'); return; }
    if (state.solved) return;
    const move = findHintMove(state.cells, state.bag, state.constraints);
    if (!move) {
      // No spend — the player has either solved everything visible or
      // the solver couldn't find anything within budget. Either way,
      // taking a hint payment for nothing actionable is wrong.
      alert('Everything looks right so far — keep placing!');
      return;
    }
    const r = await spendHint();
    if (!r.ok) {
      if (r.reason === 'no_hints') {
        const buy = confirm("You're out of hints. Buy 25 more for $0.99?");
        if (buy) setActiveCheckout({ kind: 'hint_pack' });
      } else {
        alert("Couldn't use hint. Try again in a moment.");
      }
      return;
    }
    auth.setHintsBalance(r.balance);
    // Clear any existing focus so the hint's recentlyPlaced flash isn't
    // visually competing with a different cell's accent ring.
    setFocusedCellIdx(null);
    if (move.kind === 'place') {
      dispatch({ type: 'DRAG_FROM_BAG', tileId: move.tileId, cellIdx: move.cellIdx });
    } else {
      // 'undo' hint — pick up the wrongly-placed tile back into the bag.
      // PLACE_OR_PICKUP on a placed cell with no selected tile returns
      // the tile to the bag (the reducer handles this branch).
      dispatch({ type: 'PLACE_OR_PICKUP', cellIdx: move.cellIdx });
    }
  }, [auth, state.solved, state.cells, state.bag, state.constraints]);

  // Refresh balance after returning from a Stripe-hosted hint-pack
  // checkout. Picks up `?hints_added=1` and polls /me until the
  // webhook lands. Symmetrical to the upgrade flow.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('hints_added')) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('hints_added');
    url.searchParams.delete('session_id');
    url.searchParams.delete('hints_canceled');
    window.history.replaceState({}, '', url.toString());
    let attempts = 0;
    const tick = async () => {
      const b = await fetchHintBalance();
      if (b) auth.setHintsBalance(b.balance);
      if (++attempts < 6) setTimeout(tick, 1500);
    };
    tick();
  }, [auth]);

  // Archive: pick a past (date, difficulty) to load. Only premium users
  // hit this; the header button gates on `auth.user?.isPremium` and the
  // ArchiveView itself shows a paywall for free users who somehow land
  // there.
  const handleArchivePick = useCallback((isoDate: string, difficulty: Difficulty) => {
    if (isoDate === state.puzzleDate && difficulty === state.difficulty) return;
    dispatch({ type: 'NEW_PUZZLE', difficulty, date: dateFromPuzzleDate(isoDate) });
  }, [state.puzzleDate, state.difficulty]);

  const handleJumpToToday = useCallback(() => {
    const today = dailyDateString();
    if (state.puzzleDate === today && state.history.length === 0) {
      // Already on today and nothing to clear — no-op (avoids the
      // confirm prompt that handleReset throws up).
      return;
    }
    if (state.puzzleDate !== today) {
      dispatch({ type: 'NEW_PUZZLE', difficulty: state.difficulty });
    } else {
      handleReset();
    }
  }, [state.puzzleDate, state.difficulty, state.history.length]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('upgraded')) return;
    // Strip the param so a refresh doesn't re-trigger this branch.
    const url = new URL(window.location.href);
    url.searchParams.delete('upgraded');
    url.searchParams.delete('session_id');
    url.searchParams.delete('upgrade_canceled');
    window.history.replaceState({}, '', url.toString());
    // Stripe webhook usually lands within a couple of seconds — poll
    // /me a few times so the UI flips to premium without a refresh.
    let attempts = 0;
    const tick = async () => {
      await auth.refresh();
      if (auth.user?.isPremium || ++attempts >= 6) return;
      setTimeout(tick, 1500);
    };
    tick();
  }, [auth]);

  // Cross-route entry hints. Other pages (Browse, Designer, Profile)
  // navigate to `/?archive=1` or `/?upgrade=1` when they want to defer
  // to the main app for those flows; pick that up here and open the
  // corresponding modal. The param is stripped after read so a refresh
  // doesn't re-trigger.
  //
  // 2026-05-13: deferred until auth has hydrated — handleUpgrade() reads
  // auth.user, and on the first render after a hard nav like
  // window.location.href='/?upgrade=1' the AuthContext refresh hasn't
  // resolved yet. Firing immediately would always see user=null and
  // bounce to the login modal even for signed-in users.
  const consumedEntryParamsRef = useRef(false);
  useEffect(() => {
    if (auth.loading) return;
    if (consumedEntryParamsRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const wantArchive = params.has('archive');
    const wantUpgrade = params.has('upgrade');
    const wantHints   = params.has('hints');
    if (!wantArchive && !wantUpgrade && !wantHints) return;
    consumedEntryParamsRef.current = true;
    const url = new URL(window.location.href);
    url.searchParams.delete('archive');
    url.searchParams.delete('upgrade');
    url.searchParams.delete('hints');
    window.history.replaceState({}, '', url.toString());
    if (wantArchive) setShowArchive(true);
    if (wantUpgrade) handleUpgrade();
    if (wantHints)   setActiveCheckout({ kind: 'hint_pack' });
  }, [auth.loading, handleUpgrade]);

  // ?from=<displayName> — invite-link tracking. Doesn't wait for
  // auth.loading because the value is purely cosmetic (drives the
  // "X invited you" eyebrow in AuthModal); we want to capture it the
  // moment the page loads, even before AuthContext settles. Stripped
  // from the URL after read so a refresh doesn't loop and so the
  // inviter name doesn't sit in browser history.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');
    if (!from) return;
    // Length cap matches the displayName max so we don't store an
    // arbitrarily long querystring blob in localStorage.
    const trimmed = from.trim().slice(0, 30);
    if (trimmed.length >= 3) {
      try { localStorage.setItem('hm.invitedBy', trimmed); } catch {/* ignore */}
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('from');
    window.history.replaceState({}, '', url.toString());
  }, []);

  // "Focus" a cell — click an empty/locked/placed cell with no tile
  // selected to highlight every constraint line touching it + every
  // bag tile that satisfies one of those categorical constraints. The
  // existing place/pickup behavior wins whenever a tile is selected
  // OR the user clicks a placed cell.
  const handleSelectTile = (id: string) => {
    setFocusedCellIdx(null);
    dispatch({ type: 'SELECT_TILE', tileId: id });
  };
  const handleCellClick = (i: number) => {
    if (state.selectedTileId) {
      setFocusedCellIdx(null);
      dispatch({ type: 'PLACE_OR_PICKUP', cellIdx: i });
      Haptics.tilePlaced();
      return;
    }
    const cell = state.cells[i];
    if (cell.kind === 'placed') {
      setFocusedCellIdx(null);
      dispatch({ type: 'PLACE_OR_PICKUP', cellIdx: i });
      Haptics.tilePicked();
      return;
    }
    if (cell.kind === 'gap') return;
    setFocusedCellIdx(prev => (prev === i ? null : i));
  };
  const handleUndo  = () => dispatch({ type: 'UNDO' });
  const handleReset = () => {
    const willLoseWork = state.history.length > 0 || state.solved;
    if (willLoseWork && !confirm(state.solved
      ? 'Reset this puzzle? Your finished solve will be cleared so you can replay.'
      : 'Reset this puzzle? Your placements will be cleared.')) return;
    setFocusedCellIdx(null);
    // Drop the saved solve so RESET truly starts fresh; otherwise newPuzzleState
    // would re-hydrate the solved board.
    try { localStorage.removeItem(`hm.solved.${state.difficulty}.${state.puzzleDate}`); } catch {/* ignore */}
    dispatch({ type: 'RESET' });
    // Drop the saved snapshot too so the next load doesn't re-hydrate
    // the cleared placements. Reset against the current difficulty +
    // today's date.
    if (auth.user) {
      lastSavedRef.current = '';
      clearProgress(state.puzzleDate, state.difficulty).catch(() => {/* fire-and-forget */});
    }
  };
  const handleSetDifficulty = (d: Difficulty) => {
    setFocusedCellIdx(null);
    // Picking a difficulty is an explicit "I'm moving on" gesture —
    // close the WinModal if it's still up from the previous solve so
    // the player isn't fighting a translucent overlay over the new
    // board. (When the new puzzle is unsolved, the modal would close
    // naturally on the next render anyway; this just makes the
    // transition snappier and handles the same-difficulty case too.)
    setWinModalDismissed(true);
    dispatch({ type: 'NEW_PUZZLE', difficulty: d });
  };

  // Expert is the upgrade tease — free users can SELECT it (so they
  // see the bigger grid) but the board renders blurred with an
  // upgrade overlay on top. Premium users play normally.
  const expertLocked = state.difficulty === 'Expert' && !auth.user?.isPremium;
  const handleAutofill = () => {
    const sol = solveRemaining(state.cells, state.bag, state.constraints);
    if (!sol) {
      alert('No valid completion exists from here. Try undoing a placement.');
      return;
    }
    dispatch({ type: 'AUTOFILL', cells: sol.cells, bag: sol.bag });
  };
  const remainingTiles = state.bag.filter(t => !t.placed).length;
  // Only enabled once every constraint pill is satisfied — at that point
  // autofill just slots remaining tiles into cells the constraints don't pin.
  const allConstraintsOk = evaluatedConstraints.length > 0 && evaluatedConstraints.every(c => c.status === 'ok');
  const canAutofill = !state.solved && remainingTiles > 0 && allConstraintsOk;

  // ─────────────── Drag-and-drop machinery ───────────────
  const [drag, setDrag] = useState<DragState | null>(null);
  const justDraggedRef = useRef(false);

  const handleTilePointerDown = useCallback((tile: { id: string; value: number; placed?: boolean }, e: React.PointerEvent) => {
    if (state.solved) return;
    if (tile.placed) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    setDrag({
      type: 'bag',
      tileId: tile.id,
      value: tile.value,
      startX: e.clientX, startY: e.clientY,
      x: e.clientX, y: e.clientY,
      active: false,
      overIdx: null,
      overBag: false,
    });
  }, [state.solved]);

  const handleCellPointerDown = useCallback((cellIdx: number, e: React.PointerEvent) => {
    if (state.solved) return;
    const cell = state.cells[cellIdx];
    if (!cell || cell.kind !== 'placed') return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    setDrag({
      type: 'cell',
      cellIdx,
      value: cell.modifier != null && cell.value != null
        ? (applyModifier(cell.value, cell.modifier) ?? undefined)
        : cell.value,
      rawValue: cell.value,
      startX: e.clientX, startY: e.clientY,
      x: e.clientX, y: e.clientY,
      active: false,
      overIdx: null,
      overBag: false,
    });
  }, [state.solved, state.cells]);

  useEffect(() => {
    if (!drag) return;

    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const active = drag.active || dist > DRAG_THRESHOLD;
      let overIdx: number | null = null;
      let overBag = false;
      if (active) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const cellEl = target && (target as Element).closest && (target as Element).closest('[data-cell-idx]');
        if (cellEl) {
          const idx = parseInt(cellEl.getAttribute('data-cell-idx')!, 10);
          if (!Number.isNaN(idx)) overIdx = idx;
        } else if (target && (target as Element).closest && (target as Element).closest('[data-bag-drop]')) {
          overBag = true;
        }
      }
      setDrag(d => d ? { ...d, x: e.clientX, y: e.clientY, active, overIdx, overBag } : null);
    };

    const onUp = (e: PointerEvent) => {
      const wasActive = drag.active ||
        Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) > DRAG_THRESHOLD;

      if (wasActive) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const cellEl = target && (target as Element).closest && (target as Element).closest('[data-cell-idx]');
        const bagEl  = target && (target as Element).closest && (target as Element).closest('[data-bag-drop]');
        if (cellEl) {
          const idx = parseInt(cellEl.getAttribute('data-cell-idx')!, 10);
          if (!Number.isNaN(idx)) {
            if (drag.type === 'bag' && drag.tileId) {
              dispatch({ type: 'DRAG_FROM_BAG', tileId: drag.tileId, cellIdx: idx });
              Haptics.tilePlaced();
            } else if (drag.type === 'cell' && drag.cellIdx != null && idx !== drag.cellIdx) {
              dispatch({ type: 'MOVE_CELL', fromIdx: drag.cellIdx, toIdx: idx });
              Haptics.tilePlaced();
            }
          }
        } else if (bagEl && drag.type === 'cell' && drag.cellIdx != null) {
          // Drop a placed cell onto the bag → return tile to the bag
          dispatch({ type: 'PLACE_OR_PICKUP', cellIdx: drag.cellIdx });
          Haptics.tilePicked();
        }
        justDraggedRef.current = true;
        const suppress = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); };
        document.addEventListener('click', suppress, { capture: true, once: true });
        setTimeout(() => {
          document.removeEventListener('click', suppress, { capture: true });
          justDraggedRef.current = false;
        }, 50);
      }
      setDrag(null);
    };

    const onCancel = () => setDrag(null);

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
    };
  }, [drag]);

  // While a drag is active, lock the body cursor to grabbing — without
  // this the cursor flickers between grab / pointer / default as the
  // pointer moves over different cells, which reads as twitchy.
  useEffect(() => {
    const isActive = !!drag && drag.active;
    document.body.classList.toggle('hm-dragging', isActive);
    return () => { document.body.classList.remove('hm-dragging'); };
  }, [drag]);

  // ─────────────── Dynamic board sizing ───────────────
  const boardWrapRef = useRef<HTMLDivElement | null>(null);
  const [boardBox, setBoardBox] = useState({ w: 600, h: 420 });
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onR = () => setVw(window.innerWidth);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  useEffect(() => {
    if (!boardWrapRef.current || typeof ResizeObserver === 'undefined') return;
    const el = boardWrapRef.current;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) setBoardBox({ w: width, h: height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { boardR, boardPad } = useMemo(() => {
    const size = sizeFromCellCount(state.cells.length);
    // For a hex grid of side N: numCols = 2N - 1, tallest col = 2N - 1.
    // Total board width  ≈ R * (3N - 1)        + (2N - 2) * gap
    // Total board height ≈ R * (2N - 1) * √3   + (2N - 2) * gap
    const numColsM1 = 2 * size - 2;
    const widthCoef = 3 * size - 1;
    const heightCoef = (2 * size - 1) * SQRT3;
    const minR = vw < 480 ? 18 : 22;
    const maxR = vw < 480 ? 32 : vw < 720 ? 36 : vw < 1024 ? 40 : 44;
    // padW is the horizontal SVG padding on each side — must accommodate
    // the widest constraint chip extending past the leftmost / rightmost
    // line endpoint (~90px for "Σ -10" / live "Σ -10/-10" labels).
    const padW = vw < 480 ? 78 : vw < 720 ? 78 : 78;
    const padH = vw < 480 ? 40 : vw < 720 ? 50 : 64;
    const gap = 5;
    const Rw = (boardBox.w - 2 * padW - numColsM1 * gap) / widthCoef;
    const Rh = (boardBox.h - 2 * padH - numColsM1 * gap) / heightCoef;
    let R = Math.min(Rw, Rh, maxR);
    if (!Number.isFinite(R) || R < minR) R = minR;
    return { boardR: R, boardPad: Math.max(padH, padW) };
  }, [boardBox, vw, state.cells.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault(); handleUndo();
      } else if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        setShowHelpMenu(true);
      } else if (e.key === 'Escape') {
        if (drag) setDrag(null);
        else if (focusedCellIdx != null) setFocusedCellIdx(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drag, focusedCellIdx]);

  return (
    <Fragment>
      <div className="hm-app">
        <IosAppBanner />
        <SharedHeader
          activeView="today"
          onJumpToToday={handleJumpToToday}
          onOpenArchive={() => setShowArchive(true)}
          anonStreakOverride={streakInfo.streak}
        />

        <div className="hm-main">
          <aside className="hm-rail" aria-label="Puzzle controls">
            <div className="hm-rail-meta">
              <div className="hm-eyebrow">{state.difficulty} · {state.name}</div>
              <div className="hm-rail-title">Today's hex</div>
            </div>

            <div className="hm-rail-status">
              <div className="hm-eyebrow">Status</div>
              <div className="hm-status-row">
                <span><span className="hm-status-long">Lines passing</span><span className="hm-status-short">Lines</span></span>
                <span>{linesPassing} / {evaluatedConstraints.length}</span>
              </div>
              <div className="hm-status-row">
                <span><span className="hm-status-long">Tiles placed</span><span className="hm-status-short">Tiles</span></span>
                <span>{tilesPlaced} / {state.bag.length}</span>
              </div>
              <div className="hm-status-row">
                <span>Time</span>
                <span>{formatTime(elapsed)}</span>
              </div>
            </div>

            <div className="hm-divider" />

            <div className="hm-rail-actions">
              <div className="hm-eyebrow">Actions</div>
              <div className="hm-actions">
                <button
                  className="hm-action hm-action-mobile-hide"
                  onClick={handleUndo}
                  disabled={state.history.length === 0 || state.solved}
                >
                  Undo <span className="kbd">⌘Z</span>
                </button>
                <button
                  className="hm-action hm-action-mobile-hide"
                  onClick={handleAutofill}
                  disabled={!canAutofill}
                  title="Place every remaining tile in the only valid arrangement"
                >Autofill</button>
                <button
                  className="hm-action"
                  onClick={handleHint}
                  disabled={state.solved}
                  title={auth.user
                    ? `Reveal one cell — auto-places the correct tile (${auth.user.hintsBalance} left). Click any cell with no tile selected for free guidance.`
                    : 'Sign in to use hints'}
                >
                  Hint{auth.user ? ` (${auth.user.hintsBalance})` : ''}
                </button>
                <button className="hm-action" onClick={handleReset}>Reset</button>
                <button
                  className="hm-action hm-action-mobile-hide"
                  onClick={() => setShowDailyLeaderboard(true)}
                  title="See top times across all difficulties for today's puzzle"
                >Leaderboard</button>
                <button className="hm-action hm-action-mobile-hide" onClick={() => setShowHelpMenu(true)}>
                  Help <span className="kbd">?</span>
                </button>
              </div>
            </div>

            <div className="hm-divider" />

            <div className="hm-rail-marks">
              <div className="hm-eyebrow">Marks</div>
              <MarkLegend />
            </div>

            <div className="hm-rail-difficulty">
              <div className="hm-eyebrow">Difficulty</div>
              <DifficultyTabs value={state.difficulty} onChange={handleSetDifficulty} />
            </div>
          </aside>

          <main className="hm-board-area" style={{ position: 'relative' }}>
            {/* When the user is on Expert without premium, blur the
                board + bag and overlay an upgrade CTA on top. The
                blurred content stays interactive at the DOM level,
                but `pointer-events: none` blocks any clicks landing
                on it, and `user-select: none` stops them from
                scraping numbers via select-all + copy. */}
            <div
              style={expertLocked ? {
                filter: 'blur(7px)',
                pointerEvents: 'none',
                userSelect: 'none',
                display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
              } : { display: 'contents' }}
            >
              <div className="hm-board-wrap" ref={boardWrapRef}>
                {brokenBanner && (
                  <div className="hm-banner" role="status">
                    <span className="hm-banner-dot" />
                    <span className="hm-banner-tag">{brokenBanner.tag}</span>
                    <span className="hm-banner-sep">·</span>
                    <span>{brokenBanner.msg}</span>
                  </div>
                )}
                <HexBoard
                  cells={state.cells}
                  evaluatedConstraints={evaluatedConstraints}
                  badRestr={badRestr}
                  R={boardR}
                  pad={boardPad}
                  gap={5}
                  onCellClick={handleCellClick}
                  onCellPointerDown={handleCellPointerDown}
                  clickableCells={clickableCells}
                  draggableCells={draggableCells}
                  recentlyPlaced={state.recentlyPlaced}
                  drag={drag}
                  dragOverIdx={drag && drag.active ? drag.overIdx : null}
                  onActiveConstraintChange={setActiveConstraint}
                  winEpicenter={winEpicenter}
                  hintCellIdx={hint?.cellIdx ?? null}
                  hintKind={hint?.kind ?? null}
                  focusedCellIdx={focusedCellIdx}
                />
              </div>
              <div className="hm-bag-wrap">
                <Bag
                  tiles={state.bag}
                  selectedId={state.selectedTileId}
                  onSelectTile={handleSelectTile}
                  onTilePointerDown={handleTilePointerDown}
                  draggingTileId={drag && drag.active && drag.type === 'bag' ? (drag.tileId ?? null) : null}
                  highlightedIds={highlightedTileIds}
                  hintTileId={hint?.kind === 'place' ? hint.tileId : null}
                  isReturnTarget={!!drag && drag.active && drag.type === 'cell' && !!drag.overBag}
                  tileSize={vw < 480 ? 34 : 40}
                />
              </div>
            </div>

            {expertLocked && <ExpertUpgradeOverlay onUpgrade={handleUpgrade} />}
          </main>
        </div>
      </div>

      {drag && drag.active && drag.value != null && (
        <div
          className="hm-drag-ghost"
          // translate3d hits the GPU layer so high-rate pointermove
          // updates don't compete with the rest of React's render work.
          // The trailing translate(-50%, -50%) centers the ghost on the
          // cursor regardless of its size.
          style={{ transform: `translate3d(${drag.x}px, ${drag.y}px, 0) translate(-50%, -50%)` }}
        >
          <BagTile value={drag.value} size={Math.max(36, boardR * 1.4)} />
        </div>
      )}

      <HintArrow hint={hint} />


      {showArchive && (
        <ArchiveView
          isPremium={!!auth.user?.isPremium}
          isSignedIn={!!auth.user}
          currentDate={state.puzzleDate}
          onPick={handleArchivePick}
          onClose={() => setShowArchive(false)}
          onUpgrade={() => { setShowArchive(false); handleUpgrade(); }}
        />
      )}
      {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} />}
      {showHelpMenu && (
        <HelpMenuModal
          onReplayTutorial={() => { setShowHelpMenu(false); setShowTutorial(true); }}
          onShowText={() => { setShowHelpMenu(false); setShowHelpText(true); }}
          onClose={() => setShowHelpMenu(false)}
        />
      )}
      {showHelpText && <HowToPlayModal onClose={() => setShowHelpText(false)} />}
      {showAuth && (
        <AuthModal
          initialMode={showAuth}
          onClose={() => setShowAuth(false)}
        />
      )}

      {showUpgradePicker && (
        <UpgradePicker
          onPick={pickAndCheckout}
          onClose={() => setShowUpgradePicker(false)}
        />
      )}

      {activeCheckout && (
        <CheckoutModal
          product={activeCheckout}
          onClose={() => setActiveCheckout(null)}
          onSuccess={handleCheckoutSuccess}
        />
      )}

      {showAccount && (
        <AccountModal
          onClose={() => setShowAccount(false)}
          onUpgradeRequested={() => { setShowAccount(false); setShowUpgradePicker(true); }}
          onBuyHints={() => { setShowAccount(false); setActiveCheckout({ kind: 'hint_pack' }); }}
        />
      )}

      {badgeQueue.length > 0 && (
        <BadgeAchievementModal
          badge={badgeQueue[0]}
          remaining={badgeQueue.length - 1}
          onDismiss={() => setBadgeQueue(prev => prev.slice(1))}
        />
      )}

      {/*
        Suppress the win modal whenever another modal-y surface is on
        screen — otherwise the 1.5s win-anim delay can let the player
        open (say) the archive in the gap, and the win modal then
        slides on top. The `winModalDismissed` flag isn't flipped, so
        the modal returns naturally once the other surface closes.
      */}
      {state.solved && winAnimDone && !winModalDismissed
        && !showArchive && !showAccount && !showAuth
        && !showUpgradePicker && !activeCheckout
        && !showHelpMenu && !showHelpText && !showTutorial
        && !showDailyLeaderboard
        && badgeQueue.length === 0 && (
        <WinModal
          time={state.elapsedAtSolve}
          streak={auth.user ? (auth.stats?.streak ?? 0) : streakInfo.streak}
          difficulty={state.difficulty}
          onClose={() => setWinModalDismissed(true)}
          onPlayAgain={() => {
            // The saved-solved entry + server snapshot would otherwise
            // hydrate the new puzzle right back into its solved state,
            // re-popping this same modal — clear both before re-rolling.
            try { localStorage.removeItem(`hm.solved.${state.difficulty}.${state.puzzleDate}`); } catch {/* ignore */}
            if (auth.user) {
              lastSavedRef.current = '';
              clearProgress(state.puzzleDate, state.difficulty).catch(() => {/* fire-and-forget */});
            }
            setWinModalDismissed(true);
            dispatch({ type: 'RESET' });
          }}
          onChangeDifficulty={() => {
            const order: Difficulty[] = ['Easy', 'Medium', 'Hard', 'Expert'];
            const next = order[(order.indexOf(state.difficulty) + 1) % order.length];
            handleSetDifficulty(next);
          }}
          onShowLeaderboard={() => {
            // Dismiss the win modal so the leaderboard owns the
            // dialog stack; the suppression check above keeps it
            // from re-popping behind the leaderboard.
            setWinModalDismissed(true);
            setShowDailyLeaderboard(true);
          }}
        />
      )}
      {showDailyLeaderboard && (
        <DailyLeaderboardModal
          date={state.puzzleDate}
          difficulty={state.difficulty}
          onClose={() => setShowDailyLeaderboard(false)}
        />
      )}
    </Fragment>
  );
}


// Premium tier picker — small modal that opens before redirecting to
// Stripe Checkout. Shows monthly + yearly side-by-side; clicking a tile
// redirects to the corresponding hosted Checkout. Yearly is presented
// first because that's the higher-LTV upgrade and the preferred default
// for the App Store Connect Subscription Group too.
function UpgradePicker({
  onPick,
  onClose,
}: {
  onPick: (cadence: 'monthly' | 'yearly') => void;
  onClose: () => void;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: v('paper'),
          color: v('ink'),
          border: `1px solid ${v('rule')}`,
          borderRadius: 18,
          padding: 24,
          maxWidth: 460, width: '100%',
          fontFamily: FONT_SANS,
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
          Hexponents Premium
        </div>
        <div style={{ fontSize: 13, color: v('inkSoft'), marginTop: 4 }}>
          Every past puzzle. Expert difficulty. Ad-free play.
        </div>

        <button
          onClick={() => onPick('yearly')}
          className="hm-action"
          style={{
            display: 'block', width: '100%', marginTop: 18,
            padding: '14px 16px', textAlign: 'left',
            background: v('ink'), color: v('paper'),
            border: 'none', borderRadius: 12, cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Yearly</div>
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>1 year · best value (~16% off)</div>
            </div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>$19.99</div>
          </div>
        </button>

        <button
          onClick={() => onPick('monthly')}
          className="hm-action"
          style={{
            display: 'block', width: '100%', marginTop: 10,
            padding: '14px 16px', textAlign: 'left',
            background: 'transparent', color: v('ink'),
            border: `1px solid ${v('rule')}`, borderRadius: 12, cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Monthly</div>
              <div style={{ fontSize: 11, color: v('inkMute'), marginTop: 2 }}>1 month · cancel anytime</div>
            </div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>$1.99</div>
          </div>
        </button>

        <div style={{ fontSize: 11, color: v('inkMute'), marginTop: 14, lineHeight: 1.4 }}>
          Subscriptions auto-renew until canceled. Manage anytime in the customer portal.
        </div>

        <div style={{ marginTop: 14, textAlign: 'right' }}>
          <button
            onClick={onClose}
            className="hm-action"
            style={{ background: 'transparent', border: 'none', color: v('inkMute'), cursor: 'pointer' }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

// Sits on top of a blurred Expert board for free users — frosted-glass
// card with a clear upgrade CTA. The blurred board is still visible
// behind it so the user can see the size + complexity of Expert
// without being able to read the actual numbers.
function ExpertUpgradeOverlay({ onUpgrade }: { onUpgrade: () => void }) {
  return (
    <div
      style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',  // children re-enable for the CTA only
        padding: 24,
        zIndex: 5,
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          background: v('paper'),
          color: v('ink'),
          border: `1px solid ${v('rule')}`,
          borderRadius: 18,
          padding: '24px 28px',
          maxWidth: 360, width: '100%',
          textAlign: 'center',
          boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', color: v('accent'), textTransform: 'uppercase', marginBottom: 8 }}>
          Expert · Premium
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, letterSpacing: '-0.01em' }}>
          A bigger grid, every modifier
        </div>
        <div style={{ fontSize: 13, color: v('inkSoft'), lineHeight: 1.45, marginBottom: 18 }}>
          37 cells, 7 lines per axis, the full restriction palette. Premium also unlocks every past-day puzzle.
        </div>
        <button
          type="button"
          className="hm-action"
          onClick={onUpgrade}
          style={{
            background: v('ink'),
            color: v('paper'),
            border: 'none',
            borderRadius: 12,
            padding: '12px 20px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          Upgrade to Premium
        </button>
      </div>
    </div>
  );
}
